import { hideGlobalCreateDealModal } from "@app/component/createEditEntity/CreateDealModal/store/actions";
import TourDescriptor from "@app/component/flows/types/TourDescriptor";

import { messages } from "./messages";
import {
  checkTargetFunnelExistsOnBefore,
  clickElement,
  delay,
  ensureTargetLayoutOnBefore,
  goToStepForCurrentViewOnBefore,
  skipIfPresentOnShow,
  switchToKanbanOnAfter,
  TARGET_FUNNEL_NAME,
  waitFor,
} from "./utils";

// Navbar "+" — the only navbar trigger with an explicit data-action-id (MenuItemTitle)
const ADD_MENU_TRIGGER = "[data-action-id=global-add-menu]";
// rc-menu generates data-menu-id="<uuid>-<key>"; the uuid prefix changes per mount, the
// key suffix is the stable part — hence the $= matches on this and the other menu selectors
const ADD_DEAL_ITEM = '[data-menu-id$="-addDeal"]';
// rc-menu also names the opened popup list "<uuid>-<key>-popup"
const ADD_MENU_POPUP = '[id$="-add-popup"]';
// antd Form injects the field name as the input id; #name is too generic outside the modal
const DEAL_NAME_INPUT = ".ant-modal #name";
// funnel vs stage: both are .ant-select in the same container; the antd-injected input ids
// (#funnelId/#stageId) are the only distinguishing marks, hence :has()
const FUNNEL_SELECT = "[data-id='js-funnel-staging-field'] .ant-select:has(#funnelId)";
const STAGE_SELECT = "[data-id='js-funnel-staging-field'] .ant-select:has(#stageId)";
// the modal footer's only primary button
const CREATE_DEAL_BUTTON = ".ant-modal-footer button.ant-btn-primary";
const RECORDS_MENU_TRIGGER = '[data-navbar-item="records"]';
const RECORDS_MENU_POPUP = '[id$="-records-popup"]';
const DEALS_ITEM = '[data-menu-id$="-funnelsDealsRecords"]';
// the router Link inside the item — clicking the <a> performs the real navigation to /deals
const DEALS_ITEM_LINK = '[data-menu-id$="-funnelsDealsRecords"] a';
// the deals toolbar's view toggle is the only radio group there; it has no data-* attributes
const VIEW_TOGGLE = ".ant-radio-group .ant-radio-button:has([value=kanban])";
// kanban's quick funnel filter (single-select); CSS-module fragment, no data-* available
const KANBAN_FUNNEL_SELECT = "[class*=QuickFunnelFilter] .ant-select-selector";
// antd mirrors the selected option's label into the selection item's title attribute —
// this matches only when the target funnel is the current selection
const MODAL_TARGET_FUNNEL_SELECTED = `[data-id='js-funnel-staging-field'] .ant-select-selection-item[title="${TARGET_FUNNEL_NAME}"]`;
const KANBAN_TARGET_FUNNEL_SELECTED = `[class*=QuickFunnelFilter] .ant-select-selection-item[title="${TARGET_FUNNEL_NAME}"]`;

const MENU_ANIMATION_DELAY_MS = 100;

export const proSomnusCreateDealTourConfiguration: TourDescriptor = {
  id: "mmc-prosomnus-create-deal",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      onAfter: async () => {
        // open the "+" menu for the user (antd submenu opens on click)
        clickElement(ADD_MENU_TRIGGER);
      },
      onBefore: checkTargetFunnelExistsOnBefore,
      position: "bottom",
      selector: ADD_MENU_TRIGGER,
      title: messages.step01,
    },
    {
      id: "add-deal-item",
      description: messages.step02Description,
      onAfter: async () => {
        // the real item click: runs the record-limit check, opens the modal, closes the menu
        clickElement(ADD_DEAL_ITEM);
      },
      onBefore: async () => {
        await waitFor(ADD_MENU_POPUP);
        await delay(MENU_ANIMATION_DELAY_MS);
      },
      options: {
        // anchored inside the menu popup — without this, pressing Next closes the menu first
        preventMouseDown: true,
      },
      position: "left",
      selector: ADD_DEAL_ITEM,
      title: messages.step02,
    },
    {
      id: "deal-name",
      description: messages.step03Description,
      onBefore: ensureTargetLayoutOnBefore,
      position: "right",
      selector: DEAL_NAME_INPUT,
      title: messages.step03,
    },
    {
      id: "funnel-field",
      description: messages.step04Description,
      // the funnel may come preselected (form default) — then there is nothing to wait for
      onShow: skipIfPresentOnShow(MODAL_TARGET_FUNNEL_SELECTED, "stage-field"),
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      selector: FUNNEL_SELECT,
      title: messages.step04,
      waitForEvent: {
        eventType: "internal",
        properties: { funnel: TARGET_FUNNEL_NAME },
        selector: "Using: Add New Deal -> Select Funnel",
      },
    },
    {
      id: "stage-field",
      description: messages.step05Description,
      options: {
        disableInteraction: false,
        showBackground: false,
      },
      position: "right",
      selector: STAGE_SELECT,
      title: messages.step05,
    },
    {
      id: "other-fields",
      description: messages.step06Description,
      title: messages.step06,
    },
    {
      id: "create-button",
      description: messages.step07Description,
      // force-close bypasses the discard Popconfirm; harmless no-op when the user actually
      // clicked Create and the modal already closed itself
      onAfter: async ({ store }) => {
        store.dispatch(hideGlobalCreateDealModal());
      },
      position: "top",
      selector: CREATE_DEAL_BUTTON,
      title: messages.step07,
      // a real Create click closes the modal and unmounts the anchor — without this wait the
      // tooltip would vanish with an unreachable Next; either Create or Next completes the step
      waitForEvent: {
        eventType: "click",
        selector: CREATE_DEAL_BUTTON,
      },
    },
    {
      id: "records-menu",
      description: messages.step08Description,
      onAfter: async () => {
        clickElement(RECORDS_MENU_TRIGGER);
      },
      position: "bottom",
      selector: RECORDS_MENU_TRIGGER,
      title: messages.step08,
    },
    {
      id: "deals-item",
      description: messages.step09Description,
      onAfter: async () => {
        clickElement(DEALS_ITEM_LINK);
      },
      onBefore: async () => {
        await waitFor(RECORDS_MENU_POPUP);
        await delay(MENU_ANIMATION_DELAY_MS);
      },
      options: {
        // onAfter's link click pushes /deals; without the flag MmcTour treats that push as
        // the user leaving the page and hides the tour
        expectRouteChange: true,
        // anchored inside the menu popup, same trap as add-deal-item
        preventMouseDown: true,
      },
      position: "right",
      selector: DEALS_ITEM,
      title: messages.step09,
    },
    {
      // invisible utility step: /deals lands on the last-used view — branch accordingly
      id: "view-check",
      onBefore: goToStepForCurrentViewOnBefore(VIEW_TOGGLE, "funnel-select", "goto-kanban"),
    },
    {
      id: "goto-kanban",
      description: messages.step10Description,
      onAfter: switchToKanbanOnAfter,
      options: {
        // switchView's saga pushes /deals/kanban
        expectRouteChange: true,
      },
      position: "bottom",
      selector: VIEW_TOGGLE,
      title: messages.step10,
    },
    {
      id: "funnel-select",
      description: messages.step11Description,
      onShow: skipIfPresentOnShow(KANBAN_TARGET_FUNNEL_SELECTED, "drag-drop"),
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "left",
      selector: KANBAN_FUNNEL_SELECT,
      title: messages.step11,
      // the event carries no funnel payload, so any selection advances; the message names
      // the right funnel and the onShow skip covers the already-selected case
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Deal Kanban View -> Select Funnel",
      },
    },
    {
      id: "drag-drop",
      description: messages.step12Description,
      title: messages.step12,
    },
  ],
};
